const EMPLOYMENT_TYPE_OPTIONS = [
  "Full-time",
  "Part-time",
  "Contractor",
  "Freelancer",
];

/**
 * Unwrap employment type values that were double/triple JSON-encoded by the API.
 * Returns a flat array of canonical option strings (e.g. ["Full-time"]).
 */
export function parseEmploymentTypeArray(value) {
  if (value == null) return [];

  let current = value;

  for (let attempt = 0; attempt < 8; attempt += 1) {
    if (Array.isArray(current)) {
      return current
        .flat(Infinity)
        .map((item) => parseEmploymentTypeArray(item))
        .flat()
        .map((item) => cleanEmploymentTypeToken(item))
        .filter(Boolean);
    }

    if (typeof current !== "string") return [];

    const trimmed = current.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (parsed !== current) {
        current = parsed;
        continue;
      }
    } catch {
      // Fall through to manual cleanup
    }

    return trimmed
      .split(",")
      .map((part) => cleanEmploymentTypeToken(part))
      .filter(Boolean);
  }

  return [];
}

function cleanEmploymentTypeToken(token) {
  if (token == null) return "";
  const raw = String(token)
    .replace(/\\+/g, "")
    .replace(/^["'\s]+|["'\s]+$/g, "")
    .trim();

  if (!raw) return "";

  const matchedOption = EMPLOYMENT_TYPE_OPTIONS.find(
    (option) => option.toLowerCase() === raw.toLowerCase(),
  );
  return matchedOption || raw;
}

/**
 * Preferred-role records store employment type as a single string (e.g. "Full-time").
 */
export function normalizeEmploymentTypeForRole(value, fallback = "Full-time") {
  const types = parseEmploymentTypeArray(value);
  if (types.length === 0) return fallback;
  return types[0];
}

export function sanitizePreferredRoleForPayload(role) {
  if (!role || typeof role !== "object") return role;

  return {
    ...role,
    employmentType: normalizeEmploymentTypeForRole(
      role.employmentType ?? role.employment_type,
    ),
    workSetup: Array.isArray(role.workSetup) ? role.workSetup : [],
    willRelocate: role.willRelocate === "Yes" ? "Yes" : "No",
  };
}

/**
 * Strip fields that must not be sent from the admin "chooseRoles" step.
 */
export function validateSoftSkillsEvidence(softSkills) {
  if (!Array.isArray(softSkills) || softSkills.length === 0) {
    return { valid: true };
  }

  const missingEvidence = softSkills.filter(
    (skill) => !String(skill.evidence ?? "").trim(),
  );

  if (missingEvidence.length === 0) {
    return { valid: true };
  }

  const names = missingEvidence
    .map((skill) => skill.softSkill ?? skill.name ?? "Soft skill")
    .join(", ");

  return {
    valid: false,
    message: `Evidence / example is required for: ${names}`,
  };
}

export function sanitizeChooseRolesPayload(data) {
  if (!data || typeof data !== "object") return data;

  const { roles, ...rest } = data;
  const softSkills = Array.isArray(rest.softSkills) ? rest.softSkills : [];
  const evidenceCheck = validateSoftSkillsEvidence(softSkills);
  if (!evidenceCheck.valid) {
    throw new Error(evidenceCheck.message);
  }

  return {
    softSkills,
    languages: Array.isArray(rest.languages)
      ? rest.languages.map((lang) => ({
          ...(lang.id != null ? { id: lang.id } : {}),
          name: lang.name,
          fluency: lang.fluency,
        }))
      : [],
  };
}

/**
 * Treat null, blank, numeric 0, and "0" as no URL (API often sends 0 for empty).
 */
export function coerceOptionalCredentialUrl(value) {
  if (value == null) return "";
  if (typeof value === "number" && value === 0) return "";
  const trimmed = String(value).trim();
  if (!trimmed || trimmed === "0") return "";
  return trimmed;
}

/**
 * Pick the first non-empty credential URL from a certification record.
 */
export function resolveCertificationCredentialUrl(cert) {
  if (!cert || typeof cert !== "object") return "";
  const primary = coerceOptionalCredentialUrl(cert.credentialUrl);
  if (primary) return primary;
  return coerceOptionalCredentialUrl(cert.credential_url);
}

/**
 * Normalize a website URL (prepend https:// when scheme is missing).
 */
export function normalizeWebsiteUrl(url) {
  const trimmed = coerceOptionalCredentialUrl(url);
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^\d+$/.test(trimmed)) return "";
  return `https://${trimmed}`;
}

/**
 * Returns true when empty (optional) or a valid http/https website link.
 */
export function isValidWebsiteUrl(url) {
  const trimmed = coerceOptionalCredentialUrl(url);
  if (!trimmed) return true;

  try {
    const parsed = new URL(normalizeWebsiteUrl(trimmed));
    if (!["http:", "https:"].includes(parsed.protocol)) return false;

    const hostname = parsed.hostname.toLowerCase();
    if (!hostname) return false;
    if (hostname === "localhost") return true;

    return (
      hostname.includes(".") &&
      !hostname.startsWith(".") &&
      !hostname.endsWith(".")
    );
  } catch {
    return false;
  }
}

/**
 * Validate credential / certificate URL (optional field).
 */
export function validateCredentialUrl(url) {
  const trimmed = coerceOptionalCredentialUrl(url);
  if (!trimmed) {
    return { valid: true, normalized: "" };
  }

  if (!isValidWebsiteUrl(trimmed)) {
    return {
      valid: false,
      normalized: "",
      message:
        "Please enter a valid website link (e.g. https://www.credly.com/badges/your-badge)",
    };
  }

  return { valid: true, normalized: normalizeWebsiteUrl(trimmed) };
}

const MONTH_YEAR_PATTERN = /^(0[1-9]|1[0-2])\/\d{4}$/;

export function isValidMonthYearDate(value) {
  const trimmed = String(value ?? "").trim();
  return MONTH_YEAR_PATTERN.test(trimmed);
}

/**
 * True when the certification record has a non-empty expiry date value.
 */
export function hasCertificationExpiryDate(cert) {
  const raw = cert?.expiryDate ?? cert?.expiry_date;
  if (raw == null) return false;
  const trimmed = String(raw).trim();
  return trimmed !== "" && trimmed.toLowerCase() !== "null";
}

/**
 * Derive noExpiry for API payloads: 1 = no expiry, 0 = has expiry.
 * When expiry date is missing, treat as no expiry even if noExpiry was 0 from the API.
 */
export function resolveCertificationNoExpiry(cert) {
  return hasCertificationExpiryDate(cert) ? 0 : 1;
}

/**
 * Normalize certification flags and dates before validation or save.
 */
export function normalizeCertificationEntry(cert) {
  if (!cert || typeof cert !== "object") return cert;

  const noExpiry = resolveCertificationNoExpiry(cert);
  const expiryRaw = cert?.expiryDate ?? cert?.expiry_date;
  const expiryDate =
    noExpiry === 1 ? "" : String(expiryRaw ?? "").trim();

  const urlCheck = validateCredentialUrl(
    resolveCertificationCredentialUrl(cert),
  );
  const normalizedUrl = urlCheck.valid ? urlCheck.normalized : "";

  return {
    ...cert,
    noExpiry,
    no_expiry: noExpiry,
    expiryDate,
    expiry_date: noExpiry === 1 ? null : expiryDate || null,
    credentialUrl: normalizedUrl,
    credential_url: normalizedUrl,
  };
}

export function normalizeCertifications(certifications) {
  if (!Array.isArray(certifications)) return certifications;
  return certifications.map(normalizeCertificationEntry);
}

export function validateCertificationEntry(cert) {
  const normalized = normalizeCertificationEntry(cert);
  const errors = {};
  const certificationName = String(
    normalized?.certificationName ?? normalized?.certification_name ?? "",
  ).trim();
  const issuingOrganization = String(
    normalized?.issuingOrganization ?? normalized?.issuing_organization ?? "",
  ).trim();
  const issueDate = String(
    normalized?.issueDate ?? normalized?.issue_date ?? "",
  ).trim();
  const expiryDate = String(
    normalized?.expiryDate ?? normalized?.expiry_date ?? "",
  ).trim();
  const noExpiry = resolveCertificationNoExpiry(normalized) === 1;

  if (!certificationName) {
    errors.certificationName = "Certification name is required.";
  }
  if (!issuingOrganization) {
    errors.issuingOrganization = "Issuing organization is required.";
  }
  if (!issueDate) {
    errors.issueDate = "Issue date is required.";
  } else if (!isValidMonthYearDate(issueDate)) {
    errors.issueDate = "Enter a valid issue date (MM/YYYY).";
  }
  if (!noExpiry) {
    if (!expiryDate) {
      errors.expiryDate = "Expiry date is required.";
    } else if (!isValidMonthYearDate(expiryDate)) {
      errors.expiryDate = "Enter a valid expiry date (MM/YYYY).";
    }
  }

  const urlCheck = validateCredentialUrl(
    resolveCertificationCredentialUrl(normalized),
  );
  if (!urlCheck.valid) {
    errors.credentialUrl = urlCheck.message;
  }

  const fieldNames = Object.keys(errors);
  if (fieldNames.length === 0) {
    return {
      valid: true,
      errors: {},
      normalizedUrl: urlCheck.normalized,
      normalized: normalized,
    };
  }

  const label =
    certificationName || issuingOrganization || "Certification";
  const firstMessage = errors[fieldNames[0]];

  return {
    valid: false,
    errors,
    message: `${label}: ${firstMessage}`,
  };
}

export function validateCertifications(certifications) {
  if (!Array.isArray(certifications)) return { valid: true };

  for (const cert of certifications) {
    const result = validateCertificationEntry(cert);
    if (!result.valid) {
      return { valid: false, message: result.message, errors: result.errors };
    }
  }

  return { valid: true };
}

export function validateEducationEntry(education) {
  const errors = {};
  const degree = String(education?.degree ?? "").trim();
  const fieldOfStudy = String(
    education?.fieldOfStudy ?? education?.field_of_study ?? "",
  ).trim();
  const location = String(education?.location ?? "").trim();

  if (!degree) {
    errors.degree = "Degree is required.";
  }
  if (!fieldOfStudy) {
    errors.fieldOfStudy = "Field of study is required.";
  }
  if (!location) {
    errors.location = "Institution / location is required.";
  }

  const fieldNames = Object.keys(errors);
  if (fieldNames.length === 0) {
    return { valid: true, errors: {} };
  }

  const label = degree || location || "Education";
  const firstMessage = errors[fieldNames[0]];

  return {
    valid: false,
    errors,
    message: `${label}: ${firstMessage}`,
  };
}

export function validateEducations(educations) {
  if (!Array.isArray(educations)) return { valid: true };

  for (const education of educations) {
    const result = validateEducationEntry(education);
    if (!result.valid) {
      return { valid: false, message: result.message, errors: result.errors };
    }
  }

  return { valid: true };
}

/**
 * Normalize career-details employment type before save.
 */
export function sanitizeCareerDetailsPayload(data) {
  if (!data || typeof data !== "object") return data;

  const certifications = Array.isArray(data.certifications)
    ? data.certifications.map((cert) => {
        const certCheck = validateCertificationEntry(cert);
        if (!certCheck.valid) {
          throw new Error(certCheck.message);
        }
        return {
          ...(certCheck.normalized ?? normalizeCertificationEntry(cert)),
          credentialUrl: certCheck.normalizedUrl ?? "",
          credential_url: certCheck.normalizedUrl ?? "",
        };
      })
    : data.certifications;

  const educations = Array.isArray(data.educations)
    ? data.educations.map((education) => {
        const eduCheck = validateEducationEntry(education);
        if (!eduCheck.valid) {
          throw new Error(eduCheck.message);
        }
        return education;
      })
    : data.educations;

  return {
    ...data,
    employmentType: parseEmploymentTypeArray(
      data.employmentType ?? data.employment_type,
    ),
    certifications,
    educations,
  };
}
